import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, HelpCircle, Upload, CheckCircle, ChevronDown, ChevronUp, Inbox, Send, AlertCircle, Bug, FileText, User, Settings, Sparkles, Shield, BarChart, MessageSquare } from 'lucide-react';
import { createPortal } from 'react-dom';
import supportService, { SupportTicket } from '../../services/support.service';
import { API_BASE_URL } from '../../utils/apiClient';
import styles from './HelpDrawer.module.css';

interface HelpDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

const CHIPS = [
    { icon: <Bug size={16} />,        label: 'Bug / Error',          subject: 'I found a bug or error' },
    { icon: <FileText size={16} />,   label: 'Document Issue',        subject: 'Issue with a document' },
    { icon: <User size={16} />,       label: 'User / Access Issue',   subject: 'User account or access problem' },
    { icon: <Settings size={16} />,   label: 'Settings Problem',      subject: 'Settings not working correctly' },
    { icon: <Sparkles size={16} />,   label: 'Feature Request',       subject: 'I\'d like to suggest a feature' },
    { icon: <Shield size={16} />,     label: 'Permission Problem',    subject: 'I don\'t have the right permissions' },
    { icon: <BarChart size={16} />,   label: 'Report Issue',          subject: 'Problem with a report' },
    { icon: <MessageSquare size={16} />, label: 'Other',                 subject: 'Something else' },
];

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const HelpDrawer: React.FC<HelpDrawerProps> = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState<'new' | 'mine'>('new');

    // ── Submit tab state ──
    const [selectedChip, setSelectedChip] = useState<number | null>(null);
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [dragging, setDragging] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── My Tickets tab state ──
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyText, setReplyText] = useState<Record<string, string>>({});
    const [sendingReply, setSendingReply] = useState<string | null>(null);
    const [showEmojiFor, setShowEmojiFor] = useState<string | null>(null);

    const QUICK_EMOJIS = [
        '👍', '👎', '🙏', '✅', '❌', '👀', '💡', '🔥',
        '😊', '😄', '😢', '😡', '😮', '🤔', '😅', '🥲',
        '❤️', '💔', '🎉', '🚀', '⚡', '🛠️',
    ];

    useEffect(() => {
        if (isOpen && activeTab === 'mine') loadMyTickets(1);
    }, [isOpen, activeTab]);

    const loadMyTickets = async (p: number) => {
        setTicketsLoading(true);
        try {
            const res = await supportService.getMyTickets(p, 8);
            setTickets(res.items);
            setPage(res.page);
            setTotalPages(Math.ceil(res.total / res.page_size));
        } catch {
            // silent
        } finally {
            setTicketsLoading(false);
        }
    };

    const handleSendReply = async (ticketId: string) => {
        const msg = (replyText[ticketId] || '').trim();
        if (!msg) return;
        setSendingReply(ticketId);
        try {
            await supportService.userReplyToTicket(ticketId, msg);
            setReplyText(prev => ({ ...prev, [ticketId]: '' }));
            setReplyingTo(null);
            setShowEmojiFor(null);
            await loadMyTickets(page);
        } catch {
            // silent
        } finally {
            setSendingReply(null);
        }
    };

    const handleChipClick = (idx: number) => {
        if (selectedChip === idx) {
            setSelectedChip(null);
            setSubject('');
        } else {
            setSelectedChip(idx);
            setSubject(CHIPS[idx].subject);
        }
    };

    const addFiles = (files: FileList | null) => {
        if (!files) return;
        setSubmitError('');
        const arr = Array.from(files);
        
        // Size validation: 1 MB limit
        const validFiles = arr.filter(f => {
            if (f.size > 1024 * 1024) {
                setSubmitError(prev => prev ? `${prev} | ${f.name} exceeds 1MB` : `${f.name} exceeds 1MB max size limit`);
                return false;
            }
            return true;
        });

        const newFiles = [...images, ...validFiles].slice(0, 5);
        setImages(newFiles);
        setImagePreviews(newFiles.map(f => URL.createObjectURL(f)));
    };

    const removeImage = (i: number) => {
        const newFiles = images.filter((_, idx) => idx !== i);
        setImages(newFiles);
        setImagePreviews(newFiles.map(f => URL.createObjectURL(f)));
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer.files);
    }, [images]);

    const handleSubmit = async () => {
        if (!subject.trim()) { setSubmitError('Please enter a subject or select a category.'); return; }
        setSubmitting(true);
        setSubmitError('');
        try {
            const cat = selectedChip !== null ? CHIPS[selectedChip].label : 'Other';
            await supportService.submitTicket(cat, subject, description, images);
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                setSelectedChip(null);
                setSubject('');
                setDescription('');
                setImages([]);
                setImagePreviews([]);
            }, 2500);
        } catch {
            setSubmitError('Failed to submit ticket. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <div className={styles.drawerOverlay} onClick={onClose} />
            <div className={styles.drawer}>
                {/* Header */}
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerTitle}>
                        <HelpCircle size={20} />
                        Help & Support
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}><X size={16} /></button>
                </div>

                {/* Tabs */}
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'new' ? styles.active : ''}`}
                        onClick={() => setActiveTab('new')}
                    >
                        <Send size={14} /> Submit Issue
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'mine' ? styles.active : ''}`}
                        onClick={() => { setActiveTab('mine'); loadMyTickets(1); }}
                    >
                        <Inbox size={14} /> My Tickets
                    </button>
                </div>

                {/* Body */}
                <div className={styles.drawerBody}>
                    {activeTab === 'new' && (
                        <>
                            {success && (
                                <div className={styles.successBanner}>
                                    <CheckCircle size={18} />
                                    Ticket submitted! We'll get back to you soon.
                                </div>
                            )}

                            <div className={styles.sectionLabel}>Select issue type</div>
                            <div className={styles.chipGrid}>
                                {CHIPS.map((chip, i) => (
                                    <button
                                        key={i}
                                        className={`${styles.chip} ${selectedChip === i ? styles.selected : ''}`}
                                        onClick={() => handleChipClick(i)}
                                    >
                                        <span className={styles.chipIcon}>{chip.icon}</span>
                                        {chip.label}
                                    </button>
                                ))}
                            </div>

                            <div className={styles.formGroup}>
                                <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Subject</span>
                                    <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>{subject.length}/150</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Brief description of your issue"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    maxLength={150}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Description (optional)</span>
                                    <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>{description.length}/500</span>
                                </label>
                                <textarea
                                    placeholder="Provide more details about the issue..."
                                    value={description}
                                    onChange={e => setDescription(e.target.value.substring(0, 500))}
                                    maxLength={500}
                                    rows={4}
                                />
                            </div>

                            {/* Image upload */}
                            <div className={styles.sectionLabel}>Attach Screenshots (optional)</div>
                            {images.length > 0 && (
                                <div className={styles.imagePreviewGrid}>
                                    {imagePreviews.map((src, i) => (
                                        <div className={styles.imagePreviewCard} key={i}>
                                            <img src={src} alt="" />
                                            <button className={styles.removeImageBtn} onClick={() => removeImage(i)}>×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {images.length < 5 && (
                                <div
                                    className={`${styles.dropZone} ${dragging ? styles.dragging : ''}`}
                                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                                    onDragLeave={() => setDragging(false)}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload size={22} />
                                    <span>Drag & drop images or <strong>browse</strong></span>
                                    <span style={{ fontSize: '11px', opacity: 0.6 }}>PNG, JPG, GIF up to 1 MB each · max 5</span>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className={styles.hiddenInput}
                                onChange={e => addFiles(e.target.files)}
                            />

                            {submitError && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f87171', fontSize: 13, marginTop: 8 }}>
                                    <AlertCircle size={14} /> {submitError}
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'mine' && (
                        <>
                            {ticketsLoading ? (
                                [1,2,3].map(i => (
                                    <div key={i} className={styles.skeletonCard} style={{ marginBottom: 10 }}>
                                        <div className={styles.skeletonLine} style={{ width: '65%' }} />
                                        <div className={styles.skeletonLine} style={{ width: '35%' }} />
                                        <div className={styles.skeletonLine} style={{ width: '50%', height: 10, marginTop: 4 }} />
                                    </div>
                                ))
                            ) : tickets.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <Inbox size={36} style={{ opacity: 0.3 }} />
                                    <p>No tickets yet</p>
                                    <span>Submit your first issue using the "Submit Issue" tab</span>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.ticketList}>
                                        {tickets.map(ticket => (
                                            <div
                                                key={ticket.id}
                                                className={`${styles.ticketCard} ${expandedId === ticket.id ? styles.expanded : ''}`}
                                                onClick={() => setExpandedId(expandedId === ticket.id ? null : ticket.id)}
                                            >
                                                <div className={styles.ticketCardHeader}>
                                                    <div className={styles.ticketMeta}>
                                                        <div className={styles.ticketSubject}>{ticket.subject}</div>
                                                        <div className={styles.ticketCategory}>{ticket.category}</div>
                                                        <div className={styles.ticketDate}>{formatDate(ticket.created_at)}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                                                        <span className={`${styles.statusBadge} ${styles[ticket.status]}`}>
                                                            {ticket.status.replace('_', ' ')}
                                                        </span>
                                                        {expandedId === ticket.id ? <ChevronUp size={14} style={{ color: '#6366f1' }} /> : <ChevronDown size={14} style={{ opacity: 0.4 }} />}
                                                    </div>
                                                </div>

                                                {expandedId === ticket.id && (
                                                    <>
                                                        {ticket.description && (
                                                            <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 10, marginBottom: 0 }}>{ticket.description}</p>
                                                        )}
                                                        {ticket.image_urls?.length > 0 && (
                                                            <div className={styles.ticketImages}>
                                                                {ticket.image_urls.map((url, i) => {
                                                                    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
                                                                    return (
                                                                        <img
                                                                            key={i}
                                                                            src={fullUrl}
                                                                            className={styles.ticketThumb}
                                                                            alt="attachment"
                                                                            onClick={e => { e.stopPropagation(); window.open(fullUrl, '_blank'); }}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className={styles.replyThread}>
                                                            {ticket.replies?.length === 0 ? (
                                                                <div className={styles.noReplies}>No replies yet — we'll respond soon.</div>
                                                            ) : (() => {
                                                                const visibleReplies = ticket.replies.filter(r => r.message !== 'User acknowledged.');
                                                                const isOpen = replyingTo === ticket.id;
                                                                const text = replyText[ticket.id] || '';
                                                                return (
                                                                    <>
                                                                        {visibleReplies.map((reply) => {
                                                                            const isUserReply = reply.author_id === ticket.user_id;
                                                                            return (
                                                                            <div key={reply.id} className={styles.replyBubble}>
                                                                                <div className={`${styles.replyBubbleHeader} ${isUserReply ? styles.replyBubbleHeaderUser : styles.replyBubbleHeaderSupport}`}>
                                                                                    <div className={styles.replyAuthor}>
                                                                                        <User size={11} />
                                                                                        {reply.author_name} · {formatDate(reply.created_at)}
                                                                                    </div>
                                                                                </div>
                                                                                <div className={styles.replyBubbleBody}>
                                                                                    <p>{reply.message}</p>
                                                                                </div>
                                                                            </div>
                                                                        );})}


                                                                        {/* Inline reply box */}
                                                                        {isOpen ? (
                                                                            <div className={styles.inlineReplyBox} onClick={e => e.stopPropagation()}>
                                                                                {showEmojiFor === ticket.id && (
                                                                                    <div className={styles.emojiRow}>
                                                                                        {QUICK_EMOJIS.map(emoji => (
                                                                                            <button
                                                                                                key={emoji}
                                                                                                className={styles.emojiBtn}
                                                                                                onClick={() => setReplyText(prev => ({ ...prev, [ticket.id]: (prev[ticket.id] || '') + emoji }))}
                                                                                            >{emoji}</button>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                                    <input
                                                                                    className={styles.inlineReplyInput}
                                                                                    placeholder="Write a reply..."
                                                                                    maxLength={100}
                                                                                    value={text}
                                                                                    onChange={e => setReplyText(prev => ({ ...prev, [ticket.id]: e.target.value }))}
                                                                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSendReply(ticket.id); } }}
                                                                                    autoFocus
                                                                                />
                                                                                <div className={styles.inlineReplyFooter}>
                                                                                    <button
                                                                                        className={styles.emojiToggleBtn}
                                                                                        onClick={() => setShowEmojiFor(showEmojiFor === ticket.id ? null : ticket.id)}
                                                                                        title="Emoji"
                                                                                    >😊 <span className={styles.replyCharCount}>{text.length}/100</span></button>
                                                                                    <div className={styles.inlineReplyActions}>
                                                                                        <button
                                                                                            className={styles.inlineCancelBtn}
                                                                                            onClick={e => { e.stopPropagation(); setReplyingTo(null); setShowEmojiFor(null); }}
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                        <button
                                                                                            className={styles.inlineSendBtn}
                                                                                            onClick={() => handleSendReply(ticket.id)}
                                                                                            disabled={!text.trim() || sendingReply === ticket.id}
                                                                                        >
                                                                                            <Send size={12} />
                                                                                            Send
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <button
                                                                                className={styles.replyTriggerBtn}
                                                                                onClick={e => { e.stopPropagation(); setReplyingTo(ticket.id); }}
                                                                            >
                                                                                <MessageSquare size={12} />
                                                                                Reply
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {totalPages > 1 && (
                                        <div className={styles.paginationRow}>
                                            <button className={styles.pageBtn} disabled={page <= 1} onClick={() => loadMyTickets(page - 1)}>← Prev</button>
                                            <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
                                            <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => loadMyTickets(page + 1)}>Next →</button>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Footer (only for submit tab) */}
                {activeTab === 'new' && (
                    <div className={styles.drawerFooter}>
                        <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                        <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting || success}>
                            {submitting ? <><div className={styles.spinner} /> Submitting...</> : <><Send size={14} /> Submit Ticket</>}
                        </button>
                    </div>
                )}
            </div>
        </>,
        document.body
    );
};

export default HelpDrawer;
